import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import { formatDateTime } from '@formosa/shared/utils/formatDateTime';
import MultiSelectDropdown from '../../components/MultiSelectDropdown';
import Pagination from '../../components/Pagination';
import { formatSignedProfitLoss } from '../../utils/orderDisplayFormat';
import TransactionRemarkModal from './TransactionRemarkModal';
import TransactionRemarkHistoryModal from './TransactionRemarkHistoryModal';
import {
  MEMBER_ROLE_LABELS,
  MEMBER_ROLE_OPTIONS,
  TRANSACTION_TYPE_OPTIONS,
  buildTransactionPageParams,
  defaultTransactionTimeRange,
  formatAmount,
  formatOptionalId,
  formatPhoneEmail,
  formatTransactionType,
  parseCurrentRemark,
  validateTransactionTimeRange,
} from './transactionConstants';

const DEFAULT_RANGE = defaultTransactionTimeRange();

const EMPTY_FILTERS = {
  inviterId: '',
  memberId: '',
  orderSerialId: '',
  depositOrderId: '',
  withdrawOrderId: '',
  roleId: '',
  phoneEmail: '',
  types: [],
  createTimeFromInput: DEFAULT_RANGE.createTimeFromInput,
  createTimeToInput: DEFAULT_RANGE.createTimeToInput,
};

const TABLE_COLUMNS = [
  '單號',
  '訂單流水號',
  '充值單號',
  '提現單號',
  '直屬上級ID',
  '用戶ID',
  '帳號類型',
  '用戶郵箱/手機號碼',
  '交易幣種',
  '交易類型',
  '交易金額',
  '手續費',
  '帳變金額',
  '鎖倉額度',
  '獎勵金額',
  '交易前帳戶餘額',
  '交易後帳戶餘額',
  '備註',
  '交易時間',
  '部門ID',
  '交易地址',
  '操作',
];

export default function TransactionListPage() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [remarkRow, setRemarkRow] = useState(null);
  const [historyRow, setHistoryRow] = useState(null);

  async function loadRecords(pageNo = 1, nextFilters = filters) {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.transactionPage(buildTransactionPageParams(pageNo, nextFilters));
      setRecords(data?.records || []);
      setPage(data?.current || pageNo);
      setTotalPages(data?.pages || 1);
    } catch (err) {
      setError(err.message || '載入失敗');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRecords(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updateFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function handleSearch(event) {
    event.preventDefault();
    const rangeError = validateTransactionTimeRange(
      filters.createTimeFromInput,
      filters.createTimeToInput
    );
    if (rangeError) {
      setError(rangeError);
      return;
    }
    loadRecords(1, filters);
  }

  function handleReset() {
    const reset = { ...EMPTY_FILTERS };
    setFilters(reset);
    loadRecords(1, reset);
  }

  async function handleExport(exportCurrentPageOnly) {
    const rangeError = validateTransactionTimeRange(
      filters.createTimeFromInput,
      filters.createTimeToInput
    );
    if (rangeError) {
      setError(rangeError);
      return;
    }
    setExporting(true);
    setError('');
    try {
      await adminApi.exportTransactionCsvZip({
        ...buildTransactionPageParams(page, filters),
        exportCurrentPageOnly,
        fileName: 'member_transaction',
      });
    } catch (err) {
      setError(err.message || '匯出失敗');
    } finally {
      setExporting(false);
    }
  }

  function openRemarkModal(row) {
    setRemarkRow(row);
  }

  function openHistoryModal(row) {
    setHistoryRow(row);
  }

  function handleRemarkSaved(updated) {
    if (!updated?.id) {
      return;
    }
    setRecords((prev) =>
      prev.map((row) => (row.id === updated.id ? { ...row, remark: updated.remark } : row))
    );
  }

  return (
    <div className="page-card">
      <h2>交易流水</h2>

      <form className="filter-bar filter-bar-wrap" onSubmit={handleSearch}>
        <div className="form-field">
          <label htmlFor="txInviterId">直屬上級用戶ID</label>
          <input
            id="txInviterId"
            type="number"
            value={filters.inviterId}
            onChange={(e) => updateFilter('inviterId', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="txMemberId">用戶ID</label>
          <input
            id="txMemberId"
            type="number"
            value={filters.memberId}
            onChange={(e) => updateFilter('memberId', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="txOrderSerialId">訂單流水號</label>
          <input
            id="txOrderSerialId"
            type="text"
            value={filters.orderSerialId}
            onChange={(e) => updateFilter('orderSerialId', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="txDepositOrderId">充值單號</label>
          <input
            id="txDepositOrderId"
            type="number"
            value={filters.depositOrderId}
            onChange={(e) => updateFilter('depositOrderId', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="txWithdrawOrderId">提現單號</label>
          <input
            id="txWithdrawOrderId"
            type="number"
            value={filters.withdrawOrderId}
            onChange={(e) => updateFilter('withdrawOrderId', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="txRoleId">帳號類型</label>
          <select
            id="txRoleId"
            value={filters.roleId}
            onChange={(e) => updateFilter('roleId', e.target.value)}
          >
            {MEMBER_ROLE_OPTIONS.map((item) => (
              <option key={item.value || 'all'} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field form-field--wide">
          <MultiSelectDropdown
            id="txTypes"
            label="交易類型"
            options={TRANSACTION_TYPE_OPTIONS}
            value={filters.types}
            onChange={(types) => updateFilter('types', types)}
            allLabel="全部"
            placeholder="請選擇交易類型"
          />
        </div>

        <div className="form-field">
          <label htmlFor="txPhoneEmail">用戶郵箱/手機</label>
          <input
            id="txPhoneEmail"
            type="text"
            value={filters.phoneEmail}
            onChange={(e) => updateFilter('phoneEmail', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="txCreateTimeFrom">交易時間起</label>
          <input
            id="txCreateTimeFrom"
            type="datetime-local"
            value={filters.createTimeFromInput}
            onChange={(e) => updateFilter('createTimeFromInput', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="txCreateTimeTo">交易時間迄</label>
          <input
            id="txCreateTimeTo"
            type="datetime-local"
            value={filters.createTimeToInput}
            onChange={(e) => updateFilter('createTimeToInput', e.target.value)}
          />
        </div>

        <div className="filter-actions">
          <button type="submit" className="btn btn-primary">
            搜索
          </button>
          <button type="button" className="btn btn-secondary" onClick={handleReset}>
            重置
          </button>
          <button
            type="button"
            className="btn btn-success"
            disabled={exporting || loading}
            onClick={() => handleExport(true)}
          >
            {exporting ? '匯出中…' : '匯出 Excel（當前頁面）'}
          </button>
          <button
            type="button"
            className="btn btn-success"
            disabled={exporting || loading}
            onClick={() => handleExport(false)}
          >
            {exporting ? '匯出中…' : '匯出 Excel（全部資料）'}
          </button>
        </div>
      </form>

      {loading && <p>載入中…</p>}
      {error && <div className="alert alert-error">{error}</div>}

      <div className="table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              {TABLE_COLUMNS.map((label) => (
                <th key={label}>{label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!loading && records.length === 0 ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} className="empty-cell">
                  尚無紀錄
                </td>
              </tr>
            ) : (
              records.map((row) => {
                const coinPaid = formatSignedProfitLoss(row.coinPaid ?? row.amount);
                const typeCode =
                  typeof row.type === 'string'
                    ? row.type
                    : row.type?.name || row.type?.code || row.type;
                const currentRemark = parseCurrentRemark(row.remark);

                return (
                  <tr key={row.id}>
                    <td className="mono-cell">{formatOptionalId(row.id)}</td>
                    <td className="mono-cell">{row.orderSerialId || '—'}</td>
                    <td>{formatOptionalId(row.depositOrderId)}</td>
                    <td>{formatOptionalId(row.withdrawOrderId)}</td>
                    <td>{formatOptionalId(row.inviterId)}</td>
                    <td>{formatOptionalId(row.memberId)}</td>
                    <td>{MEMBER_ROLE_LABELS[row.roleId] || row.roleId || '—'}</td>
                    <td>{formatPhoneEmail(row)}</td>
                    <td>{row.symbol || '—'}</td>
                    <td>{formatTransactionType(typeCode)}</td>
                    <td>{formatAmount(row.amount)}</td>
                    <td>{formatAmount(row.fee)}</td>
                    <td className={coinPaid.className}>{coinPaid.text}</td>
                    <td>{formatAmount(row.lockedAmount)}</td>
                    <td>{formatAmount(row.pointsPaid)}</td>
                    <td>{formatAmount(row.beforeBalance)}</td>
                    <td>{formatAmount(row.balance)}</td>
                    <td className="remark-cell">
                      <span className="remark-cell__text" title={currentRemark}>
                        {currentRemark || '—'}
                      </span>
                      <button
                        type="button"
                        className="remark-edit-btn"
                        title="修改備註"
                        onClick={() => openRemarkModal(row)}
                      >
                        ✎
                      </button>
                    </td>
                    <td>{formatDateTime(row.createTime)}</td>
                    <td>{formatOptionalId(row.departmentId)}</td>
                    <td>{row.address || '—'}</td>
                    <td className="action-cell">
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => openHistoryModal(row)}
                      >
                        備註歷程
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} onChange={(p) => loadRecords(p, filters)} />

      <TransactionRemarkModal
        open={Boolean(remarkRow)}
        row={remarkRow}
        onClose={() => setRemarkRow(null)}
        onSaved={handleRemarkSaved}
      />

      <TransactionRemarkHistoryModal
        open={Boolean(historyRow)}
        row={historyRow}
        onClose={() => setHistoryRow(null)}
      />
    </div>
  );
}
